import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card';
import { Button } from '../ui/Button';
import { adminService } from '../../lib/api';
import EntreprisesTabAdvanced from './EntreprisesTabAdvanced';
import FinancierTab from './FinancierTab';
import ConformiteTab from './ConformiteTab';
import UtilisateursTab from './UtilisateursTab';
import UtilisateursALOTab from './UtilisateursALOTab';
import ParametresTab from './ParametresTab';
import ConfigurationSMSTab from './ConfigurationSMSTab';
import HistoriqueSMSTab from './HistoriqueSMSTab';
import OverviewTabEnhanced from './OverviewTabEnhanced';
import EmployesTabAdmin from './EmployesTabAdmin';
import AnalyticsTab from './AnalyticsTab';
import SuperAdminEmployesTab from './SuperAdminEmployesTab';
import SuperAdminEntreprisesTab from './SuperAdminEntreprisesTab';
import Security2FATab from './Security2FATab';
import SecurityMonitoringTab from './SecurityMonitoringTab';
import { useAuth } from '../../contexts/AuthContext';
import Sidebar from '../Layout/Sidebar';

const AdminLayoutClean = () => {
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [stats, setStats] = useState({
    nb_entreprises: 0,
    nb_entreprises_actives: 0,
    nb_employes: 0,
    nb_demandes_today: 0,
    volume_total_month: 0
  });
  const [entreprises, setEntreprises] = useState([]);
  const [loading, setLoading] = useState(true);

  // Vérifier si l'utilisateur est Super Admin
  const isSuperAdmin = user?.role === 'SUPER_ADMIN';

  // Fonction utilitaire pour formater les devises
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-GN', {
      style: 'currency',
      currency: 'GNF'
    }).format(amount || 0);
  };

  // Fonction utilitaire pour formater les dates
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const [dashboardData, entreprisesData] = await Promise.all([
        adminService.getDashboard(),
        adminService.getEntreprises(0, 50)
      ]);
      
      setStats(dashboardData);
      setEntreprises(entreprisesData);
    } catch (error) {
      console.error('Erreur lors du chargement des données:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const menuItems = [
    // 📊 TABLEAU DE BORD
    { id: 'overview', label: 'Tableau de bord', icon: '📊', category: 'dashboard' },
    
    // 🏢 GESTION
    { id: 'entreprises', label: 'Entreprises', icon: '🏢', category: 'gestion' },
    { id: 'employes', label: 'Employés', icon: '👥', category: 'gestion' },
    { id: 'validations', label: 'Validations', icon: '✅', badge: stats.nb_entreprises - stats.nb_entreprises_actives, category: 'gestion' },
    
    // 💰 FINANCES
    { id: 'financier', label: 'Financier', icon: '💰', category: 'finance' },
    { id: 'conformite', label: 'Conformité', icon: '🛡️', category: 'finance' },
    
    // 👤 UTILISATEURS
    { id: 'utilisateurs_entreprises', label: 'Admins Entreprises', icon: '🏢', category: 'users' },
    ...(isSuperAdmin ? [
      { id: 'utilisateurs_alo', label: 'Équipe ALO', icon: '👨‍💼', category: 'users' }
    ] : []),
    
    // 📱 COMMUNICATION (Super Admin uniquement)
    ...(isSuperAdmin ? [
      { id: 'config_sms', label: 'Configuration SMS', icon: '📱', category: 'communication' },
      { id: 'historique_sms', label: 'Historique SMS', icon: '📬', category: 'communication' }
    ] : []),
    
    // 🔐 SÉCURITÉ (Super Admin uniquement)
    ...(isSuperAdmin ? [
      { id: 'security_2fa', label: '2FA', icon: '🔐', category: 'security' },
      { id: 'security_monitoring', label: 'Monitoring', icon: '📡', category: 'security' }
    ] : []),
    
    // ⚙️ SYSTÈME
    { id: 'parametres', label: 'Paramètres', icon: '⚙️', category: 'system' }
  ];

  // Composants d'onglets basiques pour les autres sections
  const ValidationsTabPlaceholder = () => {
    const ValidationsTab = require('./ValidationsTab').default;
    return <ValidationsTab />;
  };

  const FinancierTabPlaceholder = () => <FinancierTab />;

  const ConformiteTabPlaceholder = () => <ConformiteTab />;

  const UtilisateursTabPlaceholder = () => <UtilisateursTab />;

  const ParametresTabPlaceholder = () => <ParametresTab />;
  
  const ConfigurationSMSTabPlaceholder = () => <ConfigurationSMSTab />;
  
  const HistoriqueSMSTabPlaceholder = () => <HistoriqueSMSTab />;
  
  const Security2FATabPlaceholder = () => <Security2FATab />;
  
  const SecurityMonitoringTabPlaceholder = () => <SecurityMonitoringTab />;

  return (
    <div className="min-h-screen bg-gray-50 lg:flex">
      {/* Overlay mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
          data-testid="admin-sidebar-overlay"
        />
      )}

      {/* Sidebar */}
      <div
        className={`fixed inset-y-0 left-0 z-50 w-64 h-screen flex flex-col shadow-lg transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:z-auto ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}
        style={{ background: 'linear-gradient(180deg, #7C3AED 0%, #A855F7 50%, #EC4899 100%)' }}
        data-testid="admin-sidebar"
      >
        <div className="p-4 border-b border-white/20">
          {/* Bouton fermeture (mobile) */}
          <div className="flex justify-end lg:hidden -mt-1 -mr-1 mb-1">
            <button
              onClick={() => setSidebarOpen(false)}
              className="text-white hover:bg-white/10 p-2 rounded"
              data-testid="admin-sidebar-close"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className="flex flex-col items-center mb-4">
            <img 
              src="/logo-alo-advance.png" 
              alt="ALO ADVANCE" 
              className="w-auto max-h-24 lg:max-h-32 drop-shadow-lg"
            />
          </div>
          <div className="mb-3 text-center">
            <p className="text-sm text-white font-medium">Admin Dashboard</p>
            <p className="text-xs text-purple-100">{isSuperAdmin ? 'Super Admin' : 'Admin Standard'}</p>
          </div>
          
          {/* Bouton de déconnexion - Version évidente */}
          <button
            onClick={() => {
              if (window.confirm('Êtes-vous sûr de vouloir vous déconnecter ?')) {
                logout();
              }
            }}
            className="w-full flex items-center justify-center px-3 py-2.5 text-sm font-medium text-white bg-white/10 hover:bg-white/20 rounded-lg transition-colors backdrop-blur-sm border border-white/20"
          >
            <span className="mr-2 text-lg">🚪</span>
            Déconnexion
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto p-2">
          <div className="space-y-1">
            {menuItems.map((item, index) => {
              // Afficher un séparateur si la catégorie change
              const prevItem = index > 0 ? menuItems[index - 1] : null;
              const showSeparator = prevItem && prevItem.category !== item.category;
              
              return (
                <React.Fragment key={item.id}>
                  {showSeparator && (
                    <div className="py-2 px-4">
                      <div className="border-t border-white/20"></div>
                    </div>
                  )}
                  <button
                    onClick={() => { setActiveTab(item.id); setSidebarOpen(false); }}
                    className={`w-full flex items-center justify-between px-4 py-2.5 text-left transition-all rounded-lg ${
                      activeTab === item.id ? 'bg-white text-purple-600 shadow-lg' : 'text-white hover:bg-white/10'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <span className="text-lg">{item.icon}</span>
                      <span className="text-sm font-medium">{item.label}</span>
                    </div>
                    {item.badge > 0 && (
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        activeTab === item.id ? 'bg-orange-500 text-white' : 'bg-white/20 text-white'
                      }`}>
                        {item.badge}
                      </span>
                    )}
                  </button>
                </React.Fragment>
              );
            })}
          </div>
        </nav>

        {/* Info utilisateur en bas */}
        <div className="p-4 border-t border-white/20">
          <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg p-3">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-purple-600 text-sm font-bold">{user?.nom?.charAt(0) || 'A'}</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">{user?.nom || 'Admin ALO'}</p>
                <p className="text-xs text-purple-100 truncate">{user?.email}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 min-w-0 lg:h-screen lg:overflow-auto">
        {/* Barre supérieure mobile */}
        <header className="lg:hidden sticky top-0 z-30 bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 -ml-2 rounded-md text-gray-600 hover:bg-gray-100"
            data-testid="admin-menu-button"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          <img src="/logo-alo-advance.png" alt="ALO ADVANCE" className="h-9 w-auto" />
          <div className="w-8" />
        </header>
        <main className="p-4 sm:p-6">
          {activeTab === 'overview' && <OverviewTabEnhanced stats={stats} />}
          {activeTab === 'entreprises' && <EntreprisesTabAdvanced entreprises={entreprises} fetchData={fetchDashboardData} />}
          {activeTab === 'employes' && <SuperAdminEmployesTab />}
          {activeTab === 'utilisateurs_alo' && isSuperAdmin && <UtilisateursALOTab />}
          {activeTab === 'validations' && <ValidationsTabPlaceholder />}
          {activeTab === 'financier' && <FinancierTabPlaceholder />}
          {activeTab === 'conformite' && <ConformiteTabPlaceholder />}
          {activeTab === 'utilisateurs_entreprises' && <UtilisateursTabPlaceholder />}
          {activeTab === 'parametres' && <ParametresTabPlaceholder />}
          {activeTab === 'config_sms' && isSuperAdmin && <ConfigurationSMSTabPlaceholder />}
          {activeTab === 'historique_sms' && isSuperAdmin && <HistoriqueSMSTabPlaceholder />}
          {activeTab === 'security_2fa' && isSuperAdmin && <Security2FATabPlaceholder />}
          {activeTab === 'security_monitoring' && isSuperAdmin && <SecurityMonitoringTabPlaceholder />}
        </main>
      </div>
    </div>
  );
};

export default AdminLayoutClean;